import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save, Search, Filter, Syringe, CheckCircle2, XCircle } from "lucide-react";
import { APPLICATION_STATUS } from "@/lib/statusLabels";

const STATUS_LABELS = APPLICATION_STATUS;

const emptyApp = (patients, professionals) => ({
  user_id: patients[0]?.id || "",
  substance: "",
  lot: "",
  dose: "",
  via: "Intramuscular",
  location: "",
  scheduled_at: new Date(Date.now() + 3600e3).toISOString().slice(0, 16),
  professional_id: professionals[0]?.id || "",
  notes: "",
});

export default function AdminApplicationsPage() {
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null); // { id?, ...fields }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [ap, ps, pr] = await Promise.all([
        api.get("/admin/applications", { params: status ? { status } : {} }),
        api.get("/admin/patients"),
        api.get("/admin/professionals"),
      ]);
      setItems(ap.data);
      setPatients(ps.data);
      setProfessionals(pr.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar dados."); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return items;
    return items.filter((a) => (
      (a.patient_name || "").toLowerCase().includes(ql)
      || (a.patient_email || "").toLowerCase().includes(ql)
      || (a.substance || "").toLowerCase().includes(ql)
    ));
  }, [items, q]);

  const openNew = () => {
    if (patients.length === 0) { toast.error("Cadastre pacientes antes."); return; }
    setEditing(emptyApp(patients, professionals));
  };
  const openEdit = (a) => setEditing({
    ...a,
    // convert to input datetime-local format
    scheduled_at: a.scheduled_at ? a.scheduled_at.slice(0, 16) : "",
  });
  const close = () => setEditing(null);

  const save = async () => {
    const e = editing;
    if (!e.substance || !e.dose || !e.via || !e.scheduled_at) { toast.error("Preencha substância, dose, via e data/hora."); return; }
    if (!e.id && !e.user_id) { toast.error("Selecione um paciente."); return; }
    setSaving(true);
    try {
      const payload = {
        substance: e.substance,
        lot: e.lot || "",
        dose: e.dose,
        via: e.via,
        location: e.location || "",
        scheduled_at: e.scheduled_at.length === 16 ? e.scheduled_at + ":00Z" : e.scheduled_at,
        professional_id: e.professional_id || null,
        notes: e.notes || "",
      };
      if (e.id) {
        if (e.status) payload.status = e.status;
        await api.patch(`/admin/applications/${e.id}`, payload);
        toast.success("Aplicação atualizada.");
      } else {
        payload.user_id = e.user_id;
        await api.post("/admin/applications", payload);
        toast.success("Aplicação agendada.");
      }
      close();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar.");
    } finally { setSaving(false); }
  };

  const setStatusFor = async (a, newStatus) => {
    try {
      await api.patch(`/admin/applications/${a.id}`, { status: newStatus });
      toast.success("Status atualizado.");
      await load();
    } catch (_) { toast.error("Erro ao atualizar status."); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Excluir aplicação "${a.substance} ${a.dose}" de ${a.patient_name}?`)) return;
    try {
      await api.delete(`/admin/applications/${a.id}`);
      toast.success("Aplicação removida.");
      await load();
    } catch (_) { toast.error("Erro ao remover."); }
  };

  return (
    <div className="space-y-6" data-testid="admin-applications-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-gold mb-3">Assistência clínica</div>
          <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Aplicações</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Registre e acompanhe aplicações injetáveis dos pacientes. Ao agendar, o painel do paciente é atualizado automaticamente.
          </p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
          data-testid="new-application-btn">
          <Plus className="w-4 h-4" /> Nova aplicação
        </button>
      </div>

      <div className="bg-card border border-border rounded-sm p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input placeholder="Buscar por paciente ou substância…" value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-sm border border-input bg-card text-sm"
            data-testid="applications-search-input" />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 rounded-sm border border-input bg-card text-sm"
            data-testid="applications-status-filter">
            <option value="">Todos</option>
            <option value="scheduled">Agendadas</option>
            <option value="done">Realizadas</option>
            <option value="cancelled">Canceladas</option>
            <option value="no_show">Não compareceram</option>
          </select>
        </div>
        <div className="text-xs text-muted-foreground ml-auto">{filtered.length} registro{filtered.length === 1 ? "" : "s"}</div>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-4 py-3 overline text-muted-foreground">Paciente</th>
              <th className="px-4 py-3 overline text-muted-foreground">Substância · Dose</th>
              <th className="px-4 py-3 overline text-muted-foreground">Via</th>
              <th className="px-4 py-3 overline text-muted-foreground">Data/Hora</th>
              <th className="px-4 py-3 overline text-muted-foreground">Profissional</th>
              <th className="px-4 py-3 overline text-muted-foreground">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => {
              const st = STATUS_LABELS[a.status] || { label: a.status, cls: "border-border text-muted-foreground bg-muted" };
              return (
                <tr key={a.id} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors" data-testid={`application-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{a.patient_name}</div>
                    <div className="text-xs text-muted-foreground">{a.patient_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium inline-flex items-center gap-2"><Syringe className="w-3.5 h-3.5 text-accent" /> {a.substance}</div>
                    <div className="text-xs text-muted-foreground">{a.dose}{a.lot ? ` · lote ${a.lot}` : ""}</div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {a.via}{a.location ? ` · ${a.location}` : ""}
                  </td>
                  <td className="px-4 py-3">{a.scheduled_at ? new Date(a.scheduled_at).toLocaleString("pt-BR") : ""}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{a.professional_name || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block overline px-2 py-0.5 rounded-sm border ${st.cls}`}>{st.label}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      {a.status === "scheduled" && (
                        <>
                          <button onClick={() => setStatusFor(a, "done")} title="Marcar como realizada"
                            className="p-1.5 rounded-sm hover:bg-muted transition-colors" data-testid={`mark-done-${a.id}`}>
                            <CheckCircle2 className="w-4 h-4 text-success" />
                          </button>
                          <button onClick={() => setStatusFor(a, "cancelled")} title="Cancelar"
                            className="p-1.5 rounded-sm hover:bg-muted transition-colors" data-testid={`cancel-app-${a.id}`}>
                            <XCircle className="w-4 h-4 text-destructive" />
                          </button>
                        </>
                      )}
                      <button onClick={() => openEdit(a)} title="Editar"
                        className="p-1.5 rounded-sm hover:bg-muted transition-colors" data-testid={`edit-application-${a.id}`}>
                        <Pencil className="w-4 h-4 text-primary" />
                      </button>
                      <button onClick={() => remove(a)} title="Excluir"
                        className="p-1.5 rounded-sm hover:bg-muted transition-colors" data-testid={`delete-application-${a.id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">Nenhuma aplicação encontrada.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={close} data-testid="application-modal">
          <div className="bg-card border border-border rounded-sm max-w-xl w-full max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-6 border-b border-border">
              <div>
                <div className="overline text-gold mb-1">{editing.id ? "Editar aplicação" : "Nova aplicação"}</div>
                <h3 className="brand-wordmark uppercase text-xl font-bold text-primary">{editing.substance || "—"}</h3>
              </div>
              <button onClick={close} className="p-1 hover:bg-muted rounded-sm" data-testid="close-application-modal">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {!editing.id && (
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Paciente</label>
                  <select value={editing.user_id} onChange={(e) => setEditing({ ...editing, user_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    data-testid="application-patient-select">
                    {patients.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.email}</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Substância</label>
                  <input value={editing.substance} onChange={(e) => setEditing({ ...editing, substance: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    placeholder="Ex: Ozempic, Mounjaro"
                    data-testid="application-substance-input" />
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Dose</label>
                  <input value={editing.dose} onChange={(e) => setEditing({ ...editing, dose: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    placeholder="Ex: 0.5mg"
                    data-testid="application-dose-input" />
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Via</label>
                  <select value={editing.via} onChange={(e) => setEditing({ ...editing, via: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    data-testid="application-via-select">
                    {["Subcutânea", "Intramuscular", "Endovenosa", "Oral"].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Lote (opcional)</label>
                  <input value={editing.lot || ""} onChange={(e) => setEditing({ ...editing, lot: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    data-testid="application-lot-input" />
                </div>
              </div>
              <div>
                <label className="overline text-muted-foreground mb-1 block">Local de aplicação (opcional)</label>
                <input value={editing.location || ""} onChange={(e) => setEditing({ ...editing, location: e.target.value })}
                  className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                  placeholder="Ex: braço esquerdo"
                  data-testid="application-location-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Data e hora</label>
                  <input type="datetime-local" value={editing.scheduled_at}
                    onChange={(e) => setEditing({ ...editing, scheduled_at: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    data-testid="application-schedule-input" />
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Profissional (opcional)</label>
                  <select value={editing.professional_id || ""} onChange={(e) => setEditing({ ...editing, professional_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    data-testid="application-professional-select">
                    <option value="">—</option>
                    {professionals.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
              {editing.id && (
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Status</label>
                  <select value={editing.status || "scheduled"} onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    data-testid="application-status-select">
                    <option value="scheduled">Agendada</option>
                    <option value="done">Realizada</option>
                    <option value="cancelled">Cancelada</option>
                    <option value="no_show">Não compareceu</option>
                  </select>
                </div>
              )}
              <div>
                <label className="overline text-muted-foreground mb-1 block">Observações</label>
                <textarea rows={3} value={editing.notes || ""}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                  data-testid="application-notes-input" />
              </div>
            </div>
            <div className="border-t border-border p-4 flex gap-2 justify-end">
              <button onClick={close} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors" data-testid="cancel-application-btn">Cancelar</button>
              <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
                data-testid="save-application-btn">
                <Save className="w-4 h-4" /> {saving ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
